import { useSelector } from 'react-redux';
import { User } from '../../data/types';
import type {
    IAnnotation,
    IChatItem,
    IFileAttachment,
    IMcpApprovalRequest,
    IUsageInfo,
    MessageContext,
} from '../../data/types/aiTypes';
import { AppError } from '../../data/types/errors';
import { RootState } from '../configureStore';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Message } from 'yup';

// Re-export types for convenience
export type { IChatItem, IUsageInfo, IAnnotation, IMcpApprovalRequest, IFileAttachment };

export interface ConversationSummary {
    id: string;
    title: string | null;
    createdAt: number;
}

export interface ConversationMessageInfo {
    role: string;
    content: string;
}

export interface AgentState {
    // Authentication state
    auth: {
        status: 'initializing' | 'authenticated' | 'unauthenticated' | 'error';
        user: User | null;
        error: string | null;
    };

    // Chat operations state
    chat: {
        status: 'idle' | 'sending' | 'streaming' | 'error';
        messages: IChatItem[];
        currentConversationId: string | null;
        error: AppError | null;
        streamingMessageId?: string;
        recoveredInput?: string;
        recoveredAttachments?: IFileAttachment[];
        editSnapshot?: IChatItem[]; // messages removed during edit, for undo
        regenerateText?: string; // auto-resend text for regenerate/edit flows
        pendingMessages: Array<{ text: string; files?: File[] }>;
    };
    context: MessageContext | undefined;
    // Conversation history state
    conversations: {
        list: ConversationSummary[];
        isLoading: boolean;
        hasMore: boolean;
    };

    // UI coordination state
    ui: {
        chatInputEnabled: boolean; // Disable during streaming/errors
    };
}

export const useAiAgentState = () => {
    const agentState = useSelector((state: RootState | any) => state.agent as AgentState);
    return agentState;
};

const initialState: AgentState = {
    auth: {
        status: 'initializing',
        user: null,
        error: null,
    },
    chat: {
        status: 'idle',
        messages: [],
        currentConversationId: null,
        error: null,
        streamingMessageId: undefined,
        recoveredInput: undefined,
        recoveredAttachments: undefined,
        editSnapshot: undefined,
        regenerateText: undefined,
        pendingMessages: [],
    },
    context: undefined,
    conversations: {
        list: [],
        isLoading: false,
        hasMore: false,
    },
    ui: {
        chatInputEnabled: true,
    },
};

export const agentSlice = createSlice({
    name: 'agent',
    initialState: initialState,
    reducers: {
        authInitializedAction: (state, action: PayloadAction<{ user: User }>) => {
            state.auth.status = 'authenticated';
            state.auth.user = action.payload.user;
            state.auth.error = null;
        },
        authTokenExpiredAction: (state) => {
            state.auth.status = 'unauthenticated';
        },

        chatSendMessageAction: (state, action: PayloadAction<{ message: IChatItem }>) => {
            state.chat.status = 'sending';
            state.chat.messages.push(action.payload.message);
            state.chat.editSnapshot = undefined;
        },
        chatLoadMessagesAction: (state, action: PayloadAction<{ messages: IChatItem[] }>) => {
            state.chat.messages = action.payload.messages;
        },
        chatLoadConversationAction: (
            state,
            action: PayloadAction<{ conversationId: string; messages: IChatItem[] }>,
        ) => {
            state.chat.messages = action.payload.messages;
            state.chat.status = 'idle';
            state.chat.currentConversationId = action.payload.conversationId;
            state.chat.streamingMessageId = undefined;
            state.chat.error = null;
            state.chat.pendingMessages = [];
            state.chat.editSnapshot = undefined;
            state.chat.recoveredAttachments = undefined;

            state.context = undefined;

            state.ui.chatInputEnabled = true;
        },
        chatAddAssistantMessageAction: (state, action: PayloadAction<{ messageId: string }>) => {
            state.chat.messages.push({
                id: action.payload.messageId,
                role: 'assistant' as const,
                content: '',
                more: {
                    time: new Date().toISOString(),
                },
            });
        },
        
        chatStartStreamAction: (
            state,
            action: PayloadAction<{ conversationId?: string; messageId: string }>,
        ) => {
            state.chat.status = 'streaming';
            state.chat.currentConversationId = action.payload.conversationId || null;
            state.chat.streamingMessageId = action.payload.messageId;
            state.chat.error = null;

            state.ui.chatInputEnabled = true;
        },
        chatStreamChunkAction: (
            state,
            action: PayloadAction<{ messageId: string; content: string }>,
        ) => {
            const messageIndex = state.chat.messages.findIndex(
                (msg) => msg.id === action.payload.messageId,
            );

            if (messageIndex === -1) {
                // Message not found - return unchanged state
                return state;
            }

            // Create new array with updated message
           const updatedMessages = [...state.chat.messages];
            updatedMessages[messageIndex] = {
                ...updatedMessages[messageIndex],
                content: updatedMessages[messageIndex].content + action.payload.content,
            };

            state.chat.messages = updatedMessages;
        },
        chatStreamAnnotationsAction: (
            state,
            action: PayloadAction<{ messageId: string; annotations: IAnnotation[] }>,
        ) => {
            // Add annotations to the streaming message
            const messageIndex = state.chat.messages.findIndex(
                (msg) => msg.id === action.payload.messageId,
            );

            if (messageIndex === -1) {
                return state;
            }

            const updatedMessages = [...state.chat.messages];
            const existingAnnotations = updatedMessages[messageIndex].annotations || [];
            updatedMessages[messageIndex] = {
                ...updatedMessages[messageIndex],
                annotations: [...existingAnnotations, ...action.payload.annotations],
            };

            state.chat.messages = updatedMessages;
        },
        chatStreamToolUseAction: (
            state,
            action: PayloadAction<{ messageId: string; toolName: string }>,
        ) => {
            const messageIndex = state.chat.messages.findIndex(
                (msg) => msg.id === action.payload.messageId,
            );

            if (messageIndex === -1) {
                return state;
            }

            state.chat.messages[messageIndex] = {
                ...state.chat.messages[messageIndex],
                activeToolUse: action.payload.toolName,
            };
        },
        chatMCPApprovalRequestAction: (
            state,
            action: PayloadAction<{
                messageId: string;
                approvalRequest: IMcpApprovalRequest;
                previousResponseId: string | null;
            }>,
        ) => {
            const approvalMessage = {
                id: `approval-${action.payload.messageId}`,
                role: 'approval' as const,
                content: '',
                mcpApproval: {
                    ...action.payload.approvalRequest,
                    previousResponseId: action.payload.previousResponseId || '',
                },
            };

            state.chat.messages.push(approvalMessage);
            state.chat.status = 'idle';

            state.ui.chatInputEnabled = false; //Keep disabled until MCP Approval resolved
        },
        chatMCPApprovalResolvedAction: (
            state,
            action: PayloadAction<{
                approvalRequestId: string;
                resolved?: 'approved' | 'rejected';
            }>,
        ) => {
            const messageIndex = state.chat.messages.findIndex(
                (msg) =>
                    msg.role === 'approval' &&
                    msg.mcpApproval?.id === action.payload.approvalRequestId,
            );

            if (messageIndex === -1) {
                return state;
            }

            state.chat.messages[messageIndex] = {
                ...state.chat.messages[messageIndex],
                mcpApproval: {
                    ...state.chat.messages[messageIndex].mcpApproval!,
                    resolved: action.payload.resolved,
                },
            };
        },
        chatStreamCompleteAction: (state, action: PayloadAction<{ usage: IUsageInfo }>) => {
            const updatedMessages = state.chat.messages.map((msg) =>
                msg.id === state.chat.streamingMessageId
                    ? {
                          ...msg,
                          more: {
                              ...msg.more,
                              usage: action.payload.usage,
                          },
                          duration: action.payload.usage.duration,
                          retryAttempt: undefined,
                          maxRetries: undefined,
                          activeToolUse: undefined,
                      }
                    : msg,
            );
            state.chat.messages = updatedMessages;
            state.chat.status = 'idle';
            state.chat.streamingMessageId = undefined;

            state.ui.chatInputEnabled = true;
        },
        chatCancelStreamAction: (state) => {
            state.chat.status = 'idle';
            state.chat.streamingMessageId = undefined;

            state.ui.chatInputEnabled = true;
        },
        chatErrorAction: (state, action: PayloadAction<{ error: AppError }>) => {
            state.chat.status = 'error';
            state.chat.error = action.payload.error;
            state.chat.streamingMessageId = undefined;

            state.ui.chatInputEnabled = action.payload.error.recoverable;
        },
        chatClearErrorAction: (state) => {
            state.chat.error = null;
            state.chat.status = 'idle';
            state.chat.recoveredInput = undefined;
            state.chat.recoveredAttachments = undefined;

            state.ui.chatInputEnabled = true;
        },
        chatClearAction: (state) => {
            state.chat.messages = [];
            state.chat.status = 'idle';
            state.chat.currentConversationId = null;
            state.chat.streamingMessageId = undefined;
            state.chat.error = null;
            state.chat.recoveredInput = undefined;
            state.chat.recoveredAttachments = undefined;
            state.chat.pendingMessages = [];
            
            state.context = undefined;

            state.ui.chatInputEnabled = true;
        },
        chatStreamRetryAction: (
            state,
            action: PayloadAction<{ messageId: string; attempt: number; maxRetries: number }>,
        ) => {
            const retryIndex = state.chat.messages.findIndex(
                (msg) => msg.id === action.payload.messageId,
            );
            if (retryIndex === -1) return state;

            const updatedMessages = [...state.chat.messages];
            updatedMessages[retryIndex] = {
                ...updatedMessages[retryIndex],
                content: '',
                annotations: undefined,
                retryAttempt: action.payload.attempt,
                maxRetries: action.payload.maxRetries,
            };

            state.chat.messages = updatedMessages;
            state.chat.status = 'streaming';
            state.chat.streamingMessageId = action.payload.messageId;
            state.chat.error = null;

            state.ui.chatInputEnabled = true;
        },
        chatRecoverMessageAction: (
            state,
            action: PayloadAction<{ messageText: string; error: AppError; retryCount: number }>,
        ) => {
            const recoveredMessages =
                state.chat.messages.length >= 2 ? state.chat.messages.slice(0, -2) : [];

            const error = {
                ...state.chat.error,
                message: `Failed to get a response after ${action.payload.retryCount} ${action.payload.retryCount === 1 ? 'attempt' : 'attempts'}. Your message has been restored.`,
                recoverable: true,
            };

            state.chat.messages = recoveredMessages;
            state.chat.status = 'error';
            state.chat.streamingMessageId = undefined;
            state.chat.recoveredInput = action.payload.messageText;
            state.chat.error = error as AppError;

            state.ui.chatInputEnabled = true;
        },
        chatConsumedRecoveredInputAction: (state) => {
            state.chat.recoveredInput = undefined;
            state.chat.recoveredAttachments = undefined;
        },
        chatQueueMessageAction: (
            state,
            action: PayloadAction<{ text: string; files?: File[] }>,
        ) => {
            state.chat.pendingMessages.push({
                text: action.payload.text,
                files: action.payload.files,
            });
        },
        chatDequeueMessageAction: (state, action: PayloadAction<{ index: number }>) => {
            state.chat.pendingMessages.splice(action.payload.index, 1);
        },
        chatClearQueueAction: (state) => {
            state.chat.pendingMessages = [];
        },
        chatRegenerateAction: (state) => {
            // Remove the last assistant message AND its preceding user message;
            // store the user text for auto-resend so AgentChat creates a fresh pair.
            const msgs = state.chat.messages;
            const lastAssistantIdx = msgs.length - 1;
            if (lastAssistantIdx < 0 || msgs[lastAssistantIdx].role !== 'assistant') return state;

            // Find the user message that preceded the assistant message
            let lastUserIdx = -1;
            for (let i = lastAssistantIdx - 1; i >= 0; i--) {
                if (msgs[i].role === 'user') {
                    lastUserIdx = i;
                    break;
                }
            }

            const regenerateText = lastUserIdx >= 0 ? msgs[lastUserIdx].content : undefined;
            const sliceEnd = lastUserIdx >= 0 ? lastUserIdx : lastAssistantIdx;

            state.chat.messages = msgs.slice(0, sliceEnd);
            state.chat.status = 'idle';
            state.chat.streamingMessageId = undefined;
            state.chat.regenerateText = regenerateText;

            state.ui.chatInputEnabled = true;
        },
        chatEditMessageAction: (
            state,
            action: PayloadAction<{ messageId: string; newText: string }>,
        ) => {
            const targetIdx = state.chat.messages.findIndex(
                (m) => m.id === action.payload.messageId,
            );
            if (targetIdx === -1) return state;
            const targetMsg = state.chat.messages[targetIdx];

            state.chat.messages = state.chat.messages.slice(0, targetIdx);
            state.chat.status = 'idle';
            state.chat.streamingMessageId = undefined;
            state.chat.recoveredInput = action.payload.newText;
            state.chat.recoveredAttachments = targetMsg.attachments;
            state.chat.editSnapshot = state.chat.messages.slice(targetIdx);

            state.ui.chatInputEnabled = true;
        },
        chatCancelEditAction: (state) => {
            state.chat.messages = state.chat.messages.concat(state.chat.editSnapshot || []);
            state.chat.editSnapshot = undefined;
            state.chat.recoveredInput = undefined;
            state.chat.recoveredAttachments = undefined;
        },
        chatConsumedRegenerateAction: (state) => {
            state.chat.regenerateText = undefined;
        },

        conversationsLoadingAction: (state) => {
            state.conversations.isLoading = true;
        },
        conversationsLoadingDoneAction: (state) => {
            state.conversations.isLoading = false;
        },
        conversationsSetListAction: (
            state,
            action: PayloadAction<{
                conversations: ConversationSummary[];
                hasMore: boolean;
                append?: boolean;
            }>,
        ) => {
            const combined = action.payload.append
                ? [...state.conversations.list, ...action.payload.conversations]
                : action.payload.conversations;
            // Deduplicate by ID (server list order can shift between fetches)
            const seen = new Set<string>();
            const deduped = combined.filter((c) => {
                if (seen.has(c.id)) return false;
                seen.add(c.id);
                return true;
            });

            state.conversations.list = deduped;
            state.conversations.hasMore = action.payload.hasMore;
            state.conversations.isLoading = false;
        },
        contextSetContextAction: (state, action: PayloadAction<{ context: MessageContext }>) => {
            state.context = action.payload.context;
        },
        contextClearContextAction: (state) => {
            state.context = undefined;  
        }
    },
});

export const {
    authInitializedAction,
    authTokenExpiredAction,
    chatSendMessageAction,
    chatLoadMessagesAction,
    chatLoadConversationAction,
    chatAddAssistantMessageAction,
    chatStartStreamAction,
    chatStreamChunkAction,
    chatStreamAnnotationsAction,
    chatStreamToolUseAction,
    chatMCPApprovalRequestAction,
    chatMCPApprovalResolvedAction,
    chatStreamCompleteAction,
    chatCancelStreamAction,
    chatErrorAction,
    chatClearErrorAction,
    chatClearAction,
    chatStreamRetryAction,
    chatRecoverMessageAction,
    chatConsumedRecoveredInputAction,
    chatQueueMessageAction,
    chatDequeueMessageAction,
    chatClearQueueAction,
    chatRegenerateAction,
    chatEditMessageAction,
    chatCancelEditAction,
    chatConsumedRegenerateAction,
    conversationsLoadingAction,
    conversationsLoadingDoneAction,
    conversationsSetListAction,
    contextSetContextAction,
} = agentSlice.actions;

export default agentSlice.reducer;
